import { useEffect, useState } from 'react';

/**
 * Owns the sidebar/drawer accordion open-state, shared by every `NavSections`
 * instance a shell mounts (main + bottom groups today; a later drawer shell
 * mounts the same two groups again). The state must be lifted here rather
 * than owned per-`NavSections` instance — each instance renders a disjoint
 * slice of the same section list, but they all read/write the *same*
 * `accordionKeys` localStorage entry, so a single instance-scoped copy would
 * silently overwrite whatever the other instance last persisted.
 */
/**
 * Customer-sidebar sections open on a first visit (no `accordionKeys` stored
 * yet). Keys are the `eventKey`s in `side-menu-data.tsx`; after the first
 * visit the stored array is the only source of truth for these.
 */
export const DEFAULT_OPEN_SECTION_KEYS: readonly string[] = ['0', '1', '2', '3', '5'];

/**
 * Admin-console sections (`adminSideMenu.index.tsx`), forced open on EVERY
 * load regardless of what `accordionKeys` holds. Both sidebars share that one
 * localStorage entry, and every existing browser already stored a customer-
 * only array — so a default that applies only when nothing is stored would
 * never reach an existing internal user, leaving the Orders section collapsed
 * (all four view links hidden) on their first visit to /internal/global-orders.
 *
 * Decision (DV-2848 re-review N1, 2026-08-27): rather than migrate stored
 * payloads, admin sections simply don't persist a collapse. Collapsing one
 * lasts while the admin shell stays mounted; leaving into an account and
 * coming back re-expands it. Internal-only page, accepted behavior. The keys
 * are namespaced (`admin-*`) so a customer-sidebar toggle can't collide.
 */
export const ALWAYS_OPEN_SECTION_KEYS: readonly string[] = ['admin-console', 'admin-orders'];

const readInitialOpenSections = (): Set<string> => {
  const stored = localStorage.getItem('accordionKeys');
  const base: string[] = stored ? JSON.parse(stored) : [...DEFAULT_OPEN_SECTION_KEYS];
  return new Set([...base, ...ALWAYS_OPEN_SECTION_KEYS]);
};

export function useNavAccordion(): {
  openSections: Set<string>;
  toggleSection: (key: string) => void;
} {
  // Accordion open state — persisted to localStorage
  const [openSections, setOpenSections] = useState<Set<string>>(readInitialOpenSections);

  useEffect(() => {
    localStorage.setItem('accordionKeys', JSON.stringify([...openSections]));
  }, [openSections]);

  const toggleSection = (key: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return { openSections, toggleSection };
}
